import { useState, useCallback, useRef } from "react";
import "./CreateProduct.scss";
import { useMediaQuery } from "@mui/material";
import { toast } from "react-toastify";

import BlueButton from "../../../components/BlueButton/BlueButton";
import ProductHeader from "./ProductHeader";
import ControlGroup from "./ControlGroup";
import Variations from "./Variations";
import Ranges from "./Ranges";
import api from "../../../utils/api";

const CreateProduct = () => {
  // const { userData } = useSelector((state) => state.auth);
  // const user = userData?.user || userData; // Handle different structure possibilities
  const isMobile = useMediaQuery("(max-width:767px)");

  // Create proper refs for components
  const productHeaderRef = useRef(null);
  const controlGroupRef = useRef(null);
  const rangesRef = useRef(null);
  const variationsRef = useRef(null);

  // State to store form data
  const [controlGroupData, setControlGroupData] = useState(null);
  const [globalRangesData, setGlobalRangesData] = useState(null);
  const [featuresData, setFeaturesData] = useState(null);
  const [mainImage, setMainImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Cross-component interaction state
  const [controlGroupHasDiscount, setControlGroupHasDiscount] = useState(false);
  const [variationsHaveDiscount, setVariationsHaveDiscount] = useState(false);
  const [rangesActive, setRangesActive] = useState(false);
  const [variationsActive, setVariationsActive] = useState(false);

  // Use useCallback to memoize handler functions
  const handleControlGroupData = useCallback((formData) => {
    setControlGroupData(formData);
  }, []);

  const handleGlobalRangesData = useCallback((formData) => {
    setGlobalRangesData(formData);
  }, []);

  const handleFeaturesData = useCallback((formData) => {
    setFeaturesData(formData);
  }, []);

  const handleMainImage = useCallback((imageFile) => {
    setMainImage(imageFile);
  }, []);

  // Function to clear all form data
  const clearAllFormData = useCallback(() => {
    setControlGroupData(null);
    setGlobalRangesData(null);
    setFeaturesData(null);
    setMainImage(null);

    // Reset child components through refs
    if (
      productHeaderRef.current &&
      typeof productHeaderRef.current.resetImage === "function"
    ) {
      productHeaderRef.current.resetImage();
    }

    if (
      controlGroupRef.current &&
      typeof controlGroupRef.current.resetForm === "function"
    ) {
      controlGroupRef.current.resetForm();
    }

    if (
      rangesRef.current &&
      typeof rangesRef.current.resetForm === "function"
    ) {
      rangesRef.current.resetForm();
    }

    if (
      variationsRef.current &&
      typeof variationsRef.current.resetForm === "function"
    ) {
      variationsRef.current.resetForm();
    }
  }, []);

  // Submit handler for the form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const combinedFormData = new FormData();

      // Add main product image
      if (mainImage) {
        combinedFormData.append("image", mainImage);
      } else if (mainImage === null) {
        toast.error("برجاء اختيار صورة للمنتج");
        setIsLoading(false);
        return;
      } else {
        toast.error("برجاء اختيار صورة للمنتج");
        setIsLoading(false);
        return;
      }

      // Add summary (empty for new products)
      combinedFormData.append("sammary", "empty summry");

      // Add control group data
      if (controlGroupData) {
        // Copy all entries from controlGroupData to combinedFormData
        for (const [key, value] of controlGroupData.entries()) {
          combinedFormData.append(key, value);
        }
      }

      // Add global ranges data
      if (globalRangesData) {
        // Copy all entries from global ranges data to combinedFormData
        for (const [key, value] of globalRangesData.entries()) {
          combinedFormData.append(key, value);
        }
      }

      // Add features data
      if (featuresData) {
        // Copy all entries from featuresData to combinedFormData
        for (const [key, value] of featuresData.entries()) {
          combinedFormData.append(key, value);
        }
      }

      // Create new product using POST method
      await api.post("/products", combinedFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("تم إضافة المنتج بنجاح");
      window.location.reload();
      // Clear all form data after successful creation
      clearAllFormData();
    } catch (error) {
      console.error("Error creating product:", error);
      toast.error("حدث خطأ أثناء إضافة المنتج");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="create-product">
      <div className="product-container">
        <form onSubmit={handleSubmit}>
          <ProductHeader
            onImageChange={handleMainImage}
            ref={productHeaderRef}
          />
          <ControlGroup
            isMobile={isMobile}
            onDataChange={handleControlGroupData}
            onHasDiscountChange={setControlGroupHasDiscount}
            variationsHaveDiscount={variationsHaveDiscount}
            ref={controlGroupRef}
          />
          <Ranges
            isMobile={isMobile}
            isPadding={true}
            onRangesChange={handleGlobalRangesData}
            onRangesOnlyChange={setRangesActive}
            disabled={variationsActive}
            ref={rangesRef}
          />
          <Variations
            isMobile={isMobile}
            onVariationsChange={handleFeaturesData}
            onVariationsActiveChange={setVariationsActive}
            onVariationsHaveDiscountChange={setVariationsHaveDiscount}
            controlGroupHasDiscount={controlGroupHasDiscount}
            rangesOnly={rangesActive}
            ref={variationsRef}
          />
          <BlueButton
            text={isLoading ? "جاري الإضافة..." : "اضافة"}
            size={isMobile ? "full" : "large"}
            variant="fill"
            type="submit"
            disabled={isLoading}
          />
        </form>
      </div>
    </div>
  );
};

export default CreateProduct;
